import { useState, useCallback } from 'react';
import { api } from '@/lib/api';
import type {
  FindTimeResult,
  FindTimeParticipantResult,
  FreeSlot,
  CalendarEventInstance,
  SharedCalendarSummary,
} from '@/lib/api';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface BusyBlock {
  start: number; // epoch ms
  end: number; // epoch ms
}

/** Lift events to busy blocks, dropping cancelled events and clamping to a window. */
function eventsToBusy(
  events: Pick<CalendarEventInstance, 'startAt' | 'endAt' | 'status'>[],
  windowStartMs: number,
  windowEndMs: number,
): BusyBlock[] {
  const out: BusyBlock[] = [];
  for (const ev of events) {
    if (ev.status === 'cancelled') continue;
    const s = new Date(ev.startAt).getTime();
    const e = new Date(ev.endAt).getTime();
    if (e <= windowStartMs || s >= windowEndMs) continue;
    out.push({
      start: Math.max(s, windowStartMs),
      end: Math.min(e, windowEndMs),
    });
  }
  return out;
}

/** Merge overlapping/touching busy blocks into a sorted, non-overlapping list. */
function mergeBusy(blocks: BusyBlock[]): BusyBlock[] {
  if (blocks.length === 0) return [];
  const sorted = [...blocks].sort((a, b) => a.start - b.start);
  const merged: BusyBlock[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const b = sorted[i];
    if (b.start <= last.end) {
      last.end = Math.max(last.end, b.end);
    } else {
      merged.push({ ...b });
    }
  }
  return merged;
}

/**
 * Enumerate free slots of exactly `durationMs` length within
 * `[windowStart, windowEnd]`, stepping by `stepMs`. A slot is included
 * only when the entire `[start, start+duration]` interval has no
 * overlap with any merged busy block.
 */
function enumerateSlots(
  windowStartMs: number,
  windowEndMs: number,
  mergedBusy: BusyBlock[],
  durationMs: number,
  stepMs: number,
  maxSlots: number,
): FreeSlot[] {
  const slots: FreeSlot[] = [];
  // Walk gaps between merged busy blocks; emit slots at stepMs cadence within each gap.
  const gaps: BusyBlock[] = [];
  let cursor = windowStartMs;
  for (const b of mergedBusy) {
    if (b.start > cursor) gaps.push({ start: cursor, end: b.start });
    cursor = Math.max(cursor, b.end);
  }
  if (cursor < windowEndMs) gaps.push({ start: cursor, end: windowEndMs });

  for (const gap of gaps) {
    let slotStart = gap.start;
    while (slotStart + durationMs <= gap.end && slots.length < maxSlots) {
      slots.push({
        startAt: new Date(slotStart).toISOString(),
        endAt: new Date(slotStart + durationMs).toISOString(),
      });
      slotStart += stepMs;
    }
    if (slots.length >= maxSlots) break;
  }
  return slots;
}

/**
 * Compute the effective window for one participant: intersection of
 * (requested range, participant's consent.date_range). Returns null if
 * the intersection is empty (i.e. their grant doesn't overlap the
 * request) — caller treats that as `unknown-no-consent`.
 */
function effectiveWindow(
  reqStartMs: number,
  reqEndMs: number,
  consent: SharedCalendarSummary,
): { from: number; to: number } | null {
  const grantStart = consent.dateRange ? new Date(consent.dateRange.from).getTime() : -Infinity;
  const grantEnd = consent.dateRange ? new Date(consent.dateRange.to).getTime() : Infinity;
  const from = Math.max(reqStartMs, grantStart);
  const to = Math.min(reqEndMs, grantEnd);
  return to > from ? { from, to } : null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Suggest slots at 30-min cadence inside each free gap. */
const SLOT_STEP_MS = 30 * 60 * 1000;
const MAX_SLOTS = 50;

export function useFindTime() {
  const [result, setResult] = useState<FindTimeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const findTime = useCallback(
    async (
      participantUsernames: string[],
      rangeStart: string,
      rangeEnd: string,
      durationMinutes: number,
    ): Promise<FindTimeResult> => {
      setLoading(true);
      setError(null);

      const reqStartMs = new Date(rangeStart).getTime();
      const reqEndMs = new Date(rangeEnd).getTime();
      const durationMs = durationMinutes * 60 * 1000;

      try {
        // 1. Fetch the consents others have granted us (so we know who's
        //    actually scheduleable, plus their granted date_range).
        const { sharedCalendars } = await api.listSharedCalendars();
        const consentsByHolderId = new Map<string, SharedCalendarSummary>();
        for (const c of sharedCalendars) {
          // If a participant has multiple consents, prefer the broadest
          // (no date_range > any date_range). v1 keeps it simple.
          const existing = consentsByHolderId.get(c.granterHolderId);
          if (!existing || (existing.dateRange && !c.dateRange)) {
            consentsByHolderId.set(c.granterHolderId, c);
          }
        }

        // 2. Resolve each participant. Treat every failure mode as
        //    `unknown-no-consent` (opaque to avoid an enumeration oracle)
        //    EXCEPT actual fetch errors, which surface as `error`.
        const participantResults: FindTimeParticipantResult[] = [];
        const availableParticipants: Array<{
          username: string;
          holderId: string;
          window: { from: number; to: number };
        }> = [];

        for (const username of participantUsernames) {
          let resolved: { personalHolderId: string } | null = null;
          try {
            const { user } = await api.searchCalendarUsers(username);
            resolved = user;
          } catch (err) {
            participantResults.push({
              username,
              status: 'error',
              note: err instanceof Error ? err.message : 'User lookup failed',
            });
            continue;
          }
          if (!resolved) {
            participantResults.push({ username, status: 'unknown-no-consent' });
            continue;
          }
          const consent = consentsByHolderId.get(resolved.personalHolderId);
          if (!consent) {
            participantResults.push({
              username,
              status: 'unknown-no-consent',
              note: "User hasn't shared their calendar with you",
            });
            continue;
          }
          const win = effectiveWindow(reqStartMs, reqEndMs, consent);
          if (!win) {
            participantResults.push({
              username,
              status: 'unknown-no-consent',
              note: "Their share doesn't overlap the requested window",
            });
            continue;
          }
          availableParticipants.push({
            username,
            holderId: resolved.personalHolderId,
            window: win,
          });
          participantResults.push({
            username,
            status: 'available',
            holderId: resolved.personalHolderId,
            effectiveRange: {
              from: new Date(win.from).toISOString(),
              to: new Date(win.to).toISOString(),
            },
          });
        }

        // 3. Compute the global search window: intersection of the
        //    requested range and EVERY available participant's window.
        //    Ungranted ranges are not considered free — they're outside
        //    the search window entirely.
        let searchStart = reqStartMs;
        let searchEnd = reqEndMs;
        for (const p of availableParticipants) {
          if (p.window.from > searchStart) searchStart = p.window.from;
          if (p.window.to < searchEnd) searchEnd = p.window.to;
        }
        if (searchEnd - searchStart < durationMs) {
          const out: FindTimeResult = {
            participants: participantResults,
            freeSlots: [],
            searchWindow:
              searchEnd > searchStart
                ? {
                    from: new Date(searchStart).toISOString(),
                    to: new Date(searchEnd).toISOString(),
                  }
                : null,
          };
          setResult(out);
          return out;
        }

        // 4. Fetch my events for the search window + each available
        //    participant's events (clamped to their own effective range).
        //    Per-participant failures degrade the participant to `error`
        //    rather than failing the whole search.
        const myEventsPromise = api.listCalendarEvents({
          rangeStart: new Date(searchStart).toISOString(),
          rangeEnd: new Date(searchEnd).toISOString(),
        });
        const participantFetches = availableParticipants.map((p) =>
          api
            .listSharedCalendarEvents(
              p.holderId,
              new Date(Math.max(p.window.from, searchStart)).toISOString(),
              new Date(Math.min(p.window.to, searchEnd)).toISOString(),
            )
            .then((r) => ({ ok: true as const, p, events: r.events }))
            .catch((err: unknown) => ({ ok: false as const, p, err })),
        );
        const [myFetch, ...participantFetched] = await Promise.all([
          myEventsPromise.catch((err: unknown) => ({ failed: true as const, err })),
          ...participantFetches,
        ]);

        if ('failed' in myFetch) {
          throw myFetch.err instanceof Error
            ? myFetch.err
            : new Error('Failed to load your events');
        }

        const allBusy: BusyBlock[] = [];
        allBusy.push(...eventsToBusy(myFetch.events, searchStart, searchEnd));
        for (const f of participantFetched) {
          if (!f.ok) {
            // Degrade this participant to `error`, drop them from busy
            // aggregation so the search still produces a useful answer.
            const idx = participantResults.findIndex(
              (r) => r.username === f.p.username && r.status === 'available',
            );
            if (idx >= 0) {
              participantResults[idx] = {
                username: f.p.username,
                status: 'error',
                note: f.err instanceof Error ? f.err.message : 'Failed to read their calendar',
              };
            }
            continue;
          }
          allBusy.push(...eventsToBusy(f.events, searchStart, searchEnd));
        }

        const mergedBusy = mergeBusy(allBusy);
        const freeSlots = enumerateSlots(
          searchStart,
          searchEnd,
          mergedBusy,
          durationMs,
          SLOT_STEP_MS,
          MAX_SLOTS,
        );

        const out: FindTimeResult = {
          participants: participantResults,
          freeSlots,
          searchWindow: {
            from: new Date(searchStart).toISOString(),
            to: new Date(searchEnd).toISOString(),
          },
        };
        setResult(out);
        return out;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to find available times';
        setError(message);
        setResult(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, findTime, reset };
}
